import { Skeleton } from "@/components/base/Loading";
import { formatNumber, formatPercent } from "@/lib/format";
import type { SystemMetric } from "@/pages/admin/monitor/hooks/useMonitorData";

interface MonitorStatsProps {
  metrics: SystemMetric[];
  loading: boolean;
}

const toneClass: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-700",
  secondary: "bg-secondary-100 text-secondary-900",
};

export default function MonitorStats({ metrics, loading }: MonitorStatsProps) {
  const latest = metrics.length > 0 ? metrics[metrics.length - 1] : null;
  const avgUptime =
    metrics.length > 0
      ? metrics.reduce((sum, item) => sum + Number(item.uptime), 0) / metrics.length
      : null;
  const totalIssued = metrics.reduce((sum, item) => sum + item.licenses_issued, 0);
  const totalActivations = metrics.reduce((sum, item) => sum + item.activations, 0);

  const cards = [
    {
      label: "今日激活量",
      value: latest ? formatNumber(latest.activations) : "--",
      hint: "最近一天终端激活次数",
      icon: "ri-flashlight-line",
      tone: "primary",
    },
    {
      label: "今日接口请求",
      value: latest ? formatNumber(latest.api_requests) : "--",
      hint: "最近一天接口调用总量",
      icon: "ri-pulse-line",
      tone: "secondary",
    },
    {
      label: "平均可用率",
      value: formatPercent(avgUptime),
      hint: "统计区间内平台可用率",
      icon: "ri-shield-check-line",
      tone: "primary",
    },
    {
      label: "区间累计签发",
      value: formatNumber(totalIssued),
      hint: `同期累计激活 ${formatNumber(totalActivations)} 次`,
      icon: "ri-file-add-line",
      tone: "accent",
    },
  ];

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-foreground-600">{card.label}</span>
            <span className={`flex h-9 w-9 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-lg`} />
            </span>
          </div>
          {loading ? (
            <Skeleton className="mt-3 h-7 w-20" />
          ) : (
            <p className="mt-3 font-heading text-2xl font-semibold text-foreground-950">{card.value}</p>
          )}
          <p className="mt-1 text-xs text-foreground-500">{card.hint}</p>
        </div>
      ))}
    </div>
  );
}